import React, { useMemo, useState } from 'react';
import { AdminBarChart } from '../../components/admin/AdminChart';
import { AdminEmptyState, AdminMetricCard, AdminPageTitle, DateRange, Panel } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { SportMetricAnalytics } from '../../components/admin/SportMetricAnalytics';
import { sportAdoption } from '../../data/admin';
import { sports } from '../../data/sportMetrics';
import { useLocale } from '../../contexts/LocaleContext';
import type { SportId } from '../../types';
interface Row {
  id: SportId;
  sport: string;
  users: number;
  adoption: number;
  frequency: number;
  trend: number;
}

/**
 * Sports analytics driven by the central sports catalogue (data/sportMetrics `sports`).
 * A sport added to the catalogue appears here automatically: with analytics when
 * aggregated data exists, otherwise listed under "sem dados ainda" — never with fake numbers.
 */
export function AdminSports() {
  const {
    t
  } = useLocale();
  const [range, setRange] = useState('90d');
  const {
    rows,
    withoutData
  } = useMemo(() => {
    const label = (id: SportId) => t(`category.${id}`);
    const withData: Row[] = [];
    const empty: SportId[] = [];
    sports.forEach(id => {
      const data = sportAdoption.find(entry => entry.sport === id);
      if (data) withData.push({
        ...data,
        id,
        sport: label(id)
      });else empty.push(id);
    });
    withData.sort((a, b) => b.users - a.users);
    return {
      rows: withData,
      withoutData: empty
    };
  }, [t]);
  const top = rows[0];
  const columns: Column<Row>[] = [{
    id: 'sport',
    header: 'Esporte',
    sortValue: row => row.sport,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-2116" data-mp="nzwrf9m7ntt6">{row.sport}</span>
  }, {
    id: 'users',
    header: 'Usuários ativos',
    align: 'right',
    sortValue: row => row.users,
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-2117" data-mp="3rmdhr9w7sqfd">
          {row.users.toLocaleString('pt-BR')}
        </span>
  }, {
    id: 'adoption',
    header: 'Adoção',
    align: 'right',
    sortValue: row => row.adoption,
    cell: row => <span className="bee-num text-deep" data-id="element-2118" data-mp="3tdli4xoso2uv">{row.adoption}%</span>
  }, {
    id: 'frequency',
    header: 'Frequência semanal',
    align: 'right',
    sortValue: row => row.frequency,
    cell: row => <span className="bee-num text-muted" data-id="element-2119" data-mp="30p63ic9krmk2">{row.frequency.toFixed(1)}x</span>
  }, {
    id: 'trend',
    header: 'Tendência (90d)',
    align: 'right',
    sortValue: row => row.trend,
    cell: row => <span className={`bee-num font-bold ${row.trend >= 0 ? 'text-soft' : 'text-gold-deep'}`} data-id="element-2120" data-mp="3ak7qp2kaiek0">
          {row.trend >= 0 ? '+' : ''}
          {row.trend.toFixed(1)}%
        </span>
  }];
  return <div className="space-y-4" data-id="element-2121" data-mp="kxrxq7rce78j">
      <AdminPageTitle title="Performance" subtitle="Adoção e uso das métricas esportivas, sempre em agregado. Nenhuma leitura aqui é interpretação clínica." action={<DateRange value={range} onChange={setRange} data-id="element-2123" data-mp="27v17wx0tevlh" />} data-id="element-2122" data-mp="kwgv4b9mv5j0" />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2124" data-mp="1tu6yqc5y0qlr">
        <AdminMetricCard label="Usuários com esporte ativo" value="119.800" delta={4.2} hint="Ao menos uma métrica registrada" accent data-id="element-2125" data-mp="1tw1jnqh7sipu" />
        <AdminMetricCard label="Esporte mais adotado" value={top ? top.sport : '—'} hint={top ? `${top.adoption}% da base` : 'Sem dados ainda'} data-id="element-2126" data-mp="2mje3hsvzqcfg" />
        <AdminMetricCard label="Frequência média" value="3,2x/semana" delta={1.6} data-id="element-2127" data-mp="2cs9quztsf3ns" />
        <AdminMetricCard label="Esportes no catálogo" value={String(sports.length)} hint={`${rows.length} com dados · ${withoutData.length} sem dados ainda`} data-id="element-2128" data-mp="2hrcpa1f9ibms" />
      </section>

      {rows.length === 0 ? <AdminEmptyState title="Sem dados esportivos ainda" body="Nenhum esporte do catálogo tem registros agregados no período." data-id="element-2129" data-mp="3aajn8j51xov1" /> : <>
          <DataTable rows={rows} columns={columns} rowKey={row => row.id} pageSize={12} data-id="element-2130" data-mp="1gm80xxedyra1" />

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2131" data-mp="1cmhdo3o2ita9">
            <AdminBarChart title="Adoção por esporte" question="Qual modalidade concentra a base ativa?" data={rows} xKey="sport" series={[{
          id: 'users',
          name: 'Usuários ativos'
        }]} horizontal data-id="element-2132" data-mp="17nmn2fjv00fl" />
            <SportMetricAnalytics initialSport={top?.id} data-id="element-2133" data-mp="1vka4w8pbxert" />
          </div>
        </>}

      {withoutData.length > 0 && <Panel title="Esportes sem dados ainda" hint="Já fazem parte do catálogo. As análises aparecem automaticamente quando houver registros agregados." data-id="element-2134" data-mp="1qkxmc05hqtv1">
          <ul className="flex flex-wrap gap-2" data-id="element-2135" data-mp="2j9cy8dtbsw25">
            {withoutData.map(id => <li key={id} className="rounded-pill border border-dashed border-deep/25 px-3 py-[6px] text-[12.5px] font-medium text-deep" data-id="element-2136" data-mp="2qmc6j89hxsu9">
                {t(`category.${id}`)}
                <span className="ml-1 text-muted" data-id="element-2137" data-mp="30ix8fkq7yntv">· sem dados</span>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}